/**
 * Why a room would not seat this client, as far as the client is allowed to
 * know.
 *
 * `apps/game`'s `onAuth` answers **one** refusal for a bad signature, an expired
 * token, a character on somebody else's account, a character that does not
 * exist and an API that did not answer — *"telling them apart tells a stranger
 * which character ids exist and which half of a forged token to work on"*
 * (`auth/seat.ts`). This client does not try to undo that.
 *
 * What it does undo is the *other* collapse, which was this file's own. There
 * are **four** answers worth telling apart, and each of them is a different
 * screen and a different thing for a player to do:
 *
 *   - the seat was **refused** — sign in again, choose a character;
 *   - the character is **already seated** — wait a moment and ask again;
 *   - the **run** is not there to be joined — it filled up or it ended while
 *     the lobby was on screen, and the answer is to look again;
 *   - the socket never got far enough to be refused at all — the game is not
 *     answering, and a retry is what fixes it.
 *
 * Signing a player out over a matchmaking hiccup throws away a session that is
 * still perfectly good and asks for a password to fix something a retry would
 * have fixed. And telling a player the server is down when it answered them
 * precisely is the same failure pointed the other way — which is exactly what
 * this file did until 2026-08-13 (`TODO.md` #443).
 *
 * ## Why `already-seated` is not just another `unreachable`
 *
 * `descent.room.ts` answers a second connection playing the same character with
 * **409 `character-already-on-this-floor`**, deliberately a different answer
 * from the seat refusal: *"it is not a claim about who they are, they have
 * already proved that, and a client that cannot tell 'your token is bad' from
 * 'you are already in there' cannot show either"*. Folding it into
 * `unreachable` told the player **"The game server could not be reached. It may
 * be restarting"** about a server that had just answered them by name.
 *
 * It is not a rare case. A dropped socket holds the seat for
 * `RECONNECTION_WINDOW_SECONDS` (30) and `pagehide` is best effort, so a player
 * who reloads after a wifi blip meets this for up to half a minute — and
 * Descend is the one button that produces it.
 *
 * ## The client does not retry by itself
 *
 * Decided here rather than left open. A 409 is not noise on the line: it is a
 * *state* on the server, held by a timer this client cannot see and cannot
 * shorten, and every automatic attempt before that timer expires is answered
 * the same way. So an auto-retry is either a spinner the player cannot
 * interrupt for up to thirty seconds, or a burst of joins the room refuses one
 * after another — and on the reading where the seat is *not* stale, because the
 * character really is being played in another tab, it is a client quietly
 * trying to take a seat away from a live game.
 *
 * Saying so and offering the button again is the honest version of the same
 * thing: it costs one press, it keeps the wait visible and interruptible, and
 * the screen that shows it (`ui/town-screen.tsx`) is the one screen with a way
 * out of itself. It also needs no latch — the caller's `descending` flag is
 * cleared by the refusal and the button is live again, where an auto-retry
 * would need a timer whose failure mode is a dead button.
 *
 * ## Why `run-gone` is not just another `unreachable` either
 *
 * The same argument, arriving from the matchmaker instead of from the room, and
 * it is the reason this file changed a third time. `client.joinById` is answered
 * **522** — `ErrorCode.MATCHMAKE_INVALID_ROOM_ID` — for three different rooms:
 * one that is `locked`, one that was `not found`, and one that `has been
 * disposed` (`@colyseus/core`'s `MatchMaker`). Two of those three are the
 * ordinary end of a run somebody was looking at a moment ago: the fourth player
 * sat down while the lobby was on screen, or the party died and the room went
 * away.
 *
 * Reporting that as *"the game server could not be reached. It may be
 * restarting"* is the same lie the 409 and the run-end close each told once
 * before — a healthy matchmaker, answering precisely, described to the player as
 * an outage. It is also the one refusal with an obvious next move, and the
 * screen it lands on now has the control for it: **read the list again.**
 *
 * The three are deliberately *not* told apart from each other, because the
 * matchmaker does not tell them apart — the code is identical and the message is
 * free text. So the sentence names both live possibilities and the one action
 * that settles which it was.
 *
 * ## The four codes, and why they are written here rather than imported
 *
 * `apps/web` does not depend on `apps/game` — nothing in this package may, and
 * the deployment has them in separate containers. So the numbers are restated,
 * which is a real cost and is why `e2e/account.spec.ts` proves the behaviour
 * against a live room rather than against these constants.
 */

/**
 * What `apps/game`'s `SEAT_REFUSED_CODE` is: a plain 401, because from the
 * client's side the answer really is *you are not authenticated for this*.
 */
export const SEAT_REFUSED_CODE = 401;

/**
 * Colyseus's own `ErrorCode.AUTH_FAILED`.
 *
 * The framework throws this when `onAuth` returns something falsy or throws
 * anything that is not a `ServerError` — which is what a game process started
 * without a signing key does, since `readAuthConfig` throws rather than seating
 * anybody. Same answer to the player either way: this session did not get in.
 */
export const AUTH_FAILED_CODE = 525;

/**
 * What `apps/game`'s `CHARACTER_SEATED_CODE` is: a 409, because the request
 * conflicts with a seat that already exists rather than with anything about who
 * is asking.
 */
export const CHARACTER_SEATED_CODE = 409;

/**
 * Colyseus's own `ErrorCode.MATCHMAKE_INVALID_ROOM_ID`.
 *
 * The matchmaker's answer to `joinById` for a room that is **locked**, one that
 * was **not found**, and one that **has been disposed** — three states, one
 * code, one free-text message this client does not read (`@colyseus/core`'s
 * `MatchMaker.joinById`). Two of them are an ordinary run ending or filling,
 * which is why it is not an outage. See the header.
 */
export const ROOM_GONE_CODE = 522;

export type JoinRefusal = 'unauthorised' | 'already-seated' | 'run-gone' | 'unreachable';

/**
 * Every refusal there is, for a caller that has to be exhaustive about them.
 *
 * Written out rather than derived, so that a test which walks it is comparing
 * two definitions instead of one with itself.
 */
export const JOIN_REFUSALS = Object.freeze(['unauthorised', 'already-seated', 'run-gone', 'unreachable'] as const);

/** The numeric `code` an error carries, or `null` — everything here arrived from a network. */
const codeOf = (error: unknown): number | null => {
  if (typeof error !== 'object' || error === null) return null;

  const { code } = error as { readonly code?: unknown };

  return typeof code === 'number' ? code : null;
};

export const refusalOf = (error: unknown): JoinRefusal => {
  const code = codeOf(error);

  if (code === SEAT_REFUSED_CODE || code === AUTH_FAILED_CODE) return 'unauthorised';
  if (code === CHARACTER_SEATED_CODE) return 'already-seated';
  if (code === ROOM_GONE_CODE) return 'run-gone';

  return 'unreachable';
};

/**
 * What a player is told.
 *
 * No message repeats the room's own word. *"Could not reach the game server:
 * unauthorised"* over a blank canvas is precisely the failure this exists to
 * replace — it reads as a broken deployment rather than as a session that
 * ended, and it is jargon aimed at whoever is holding the logs rather than at
 * whoever is trying to play.
 *
 * The `already-seated` sentence names the wait rather than the status, because
 * the wait is the only part a player can act on: the seat frees itself, and the
 * next press works.
 *
 * `run-gone` names **both** things it can be, because the matchmaker genuinely
 * does not say which, and then names the one action that settles it — reading
 * the list again, which is a control on the very screen this sentence lands on.
 */
export const describeJoinFailure = (refusal: JoinRefusal): string => {
  switch (refusal) {
    case 'unauthorised':
      return 'The world would not accept this session. Sign in again to keep playing.';
    case 'already-seated':
      return 'This character is already in a run. A connection that dropped keeps the seat for up to half a minute — wait a moment, then descend again.';
    case 'run-gone':
      return 'That run is gone or full — it filled up or ended while you were looking at it. Refresh the list and pick another.';
    case 'unreachable':
      return 'The game server could not be reached. It may be restarting — try again in a moment.';
  }
};
